import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { type UploadResponse, uploadConsultation } from '@/api/consultations'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Textarea } from '@/components/ui/textarea'
import { describeErrorMessage } from '@/lib/errors'
import { type UploadFormValues, uploadSchema } from '@/lib/validation/upload'

export const Route = createFileRoute('/seller/upload')({
  component: UploadPage,
})

function todayISO(): string {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function UploadPage() {
  const navigate = useNavigate()
  const [progress, setProgress] = useState(0)
  const [serverMessage, setServerMessage] = useState<string | null>(null)

  const form = useForm<UploadFormValues>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      audio: undefined as unknown as File,
      consultationDate: todayISO(),
      comment: '',
    },
    mode: 'onSubmit',
  })

  const mutation = useMutation({
    mutationFn: (values: UploadFormValues) =>
      uploadConsultation({
        file: values.audio,
        consultationDate: values.consultationDate,
        comment: values.comment?.trim() || undefined,
        onProgress: setProgress,
      }),
    onSuccess: (_data: UploadResponse) => {
      setServerMessage(null)
      navigate({ to: '/seller/dashboard' })
    },
    onError: (err: Error) => {
      setServerMessage(describeErrorMessage(err, 'Не удалось загрузить запись.'))
      setProgress(0)
    },
  })

  const onSubmit = (values: UploadFormValues) => {
    setServerMessage(null)
    setProgress(0)
    mutation.mutate(values)
  }

  const audioError = form.formState.errors.audio
  const dateError = form.formState.errors.consultationDate
  const commentError = form.formState.errors.comment

  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6 pb-24">
      <Button
        type="button"
        variant="ghost"
        className="-ml-3 w-fit gap-2 text-muted-foreground"
        onClick={() => navigate({ to: '/seller/dashboard' })}
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Назад
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Загрузить запись</CardTitle>
          <CardDescription>
            Аудио из WhatsApp и не только: mp3, m4a, mp4, wav до 200 МБ. Длительность: от 30 секунд
            до 90 минут.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(onSubmit)}
            aria-busy={mutation.isPending || undefined}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="audio">Файл консультации</Label>
              <Input
                id="audio"
                type="file"
                accept="audio/*,video/mp4,.mp3,.m4a,.mp4,.wav"
                aria-invalid={audioError !== undefined || undefined}
                aria-describedby={audioError ? 'audio-error' : undefined}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  form.setValue('audio', file as File, { shouldValidate: true })
                }}
              />
              {audioError ? (
                <p id="audio-error" role="alert" className="text-sm text-destructive">
                  {audioError.message}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="consultation_date">Дата консультации</Label>
              <Input
                id="consultation_date"
                type="date"
                aria-invalid={dateError !== undefined || undefined}
                aria-describedby={dateError ? 'consultation-date-error' : undefined}
                {...form.register('consultationDate')}
              />
              {dateError ? (
                <p id="consultation-date-error" role="alert" className="text-sm text-destructive">
                  {dateError.message}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="comment">Комментарий (необязательно)</Label>
              <Textarea
                id="comment"
                placeholder="Особенности консультации, заметки тренеру"
                aria-invalid={commentError !== undefined || undefined}
                aria-describedby={commentError ? 'comment-error' : undefined}
                {...form.register('comment')}
              />
              {commentError ? (
                <p id="comment-error" role="alert" className="text-sm text-destructive">
                  {commentError.message}
                </p>
              ) : null}
            </div>

            {mutation.isPending ? (
              <div className="flex flex-col gap-2">
                <Progress value={progress} aria-label="Прогресс загрузки" />
                <p className="text-sm text-muted-foreground">Загрузка: {progress}%</p>
              </div>
            ) : null}

            {serverMessage ? (
              <p role="alert" className="text-sm text-destructive">
                {serverMessage}
              </p>
            ) : null}

            <Button type="submit" size="lg" disabled={mutation.isPending}>
              {mutation.isPending ? 'Загружаем…' : 'Загрузить запись'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  )
}
